import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from '../../../../../common/services/api'
import { showToast } from '../../../../../common/utils'
import { ALERTS_QUERY_KEY, ALERTS_URL } from '../constants'
import type { UpdateAlertBody } from '../utils/types'

type UseAlertUpdateProps = {
  onSuccess?: () => void
}

type UseAlertUpdateReturn = {
  updateAlert: (values: UpdateAlertBody) => Promise<unknown>
  isUpdateLoading: boolean
}

export const useAlertUpdate = ({
  onSuccess
}: UseAlertUpdateProps): UseAlertUpdateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: any) => {
    showToast({
      title: (err?.message || err?.msg || 'Something went wrong') as string,
      type: 'error'
    })
  }

  const updateFunction = async ({ id, ...rest }: UpdateAlertBody) =>
    postJSON({
      url: `${ALERTS_URL}/${id}`,
      method: 'PUT',
      json: rest,
      responseType: 'text'
    })

  const { mutateAsync: updateMutation, isLoading: isUpdateLoading } =
    useMutation<unknown, unknown, UpdateAlertBody, unknown>(
      [ALERTS_QUERY_KEY, 'update'],
      updateFunction,
      {
        onSuccess: () => {
          showToast({ title: 'Alert updated', type: 'success' })
          queryClient.invalidateQueries([ALERTS_QUERY_KEY])
          onSuccess?.()
        },
        onError: (err) => handleError(err)
      }
    )

  const updateAlert = async (values: UpdateAlertBody) => {
    try {
      return await updateMutation(values)
    } catch (err) {
      return err
    }
  }

  return { updateAlert, isUpdateLoading }
}
